import gql from 'graphql-tag';
import className from 'classnames/bind';
import { useRef } from 'react';
import { VideoPlaylistFragment } from '@graphqlTypes';
import { Container, RevealWrapper, SectionHeader } from '@components';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useClientMount } from '@/providers/ClientMountProvider';

import styles from './VideoPlaylist.module.scss';
import VideoPlaylistDesktop from './VideoPlaylistDesktop/VideoPlaylistDesktop';
import VideoPlaylistMobile from './VideoPlaylistMobile/VideoPlaylistMobile';
const cx = className.bind(styles);

type Props = Omit<VideoPlaylistFragment, '__typename' | 'primaryVideo' | 'videos'> & {
  primaryVideo?: Omit<VideoPlaylistFragment['primaryVideo'], '__typename'>;
  videos?: Omit<VideoPlaylistFragment['videos'][number], '__typename'>[];
  largeTitle?: boolean;
};

export default function Video(props: Props) {
  const { eyebrow, headline, body, largeTitle } = props;
  const showHeader = Boolean(eyebrow || headline || body);
  const { mounted } = useClientMount();

  // -------------------- Animation

  const rootEl = useRef<HTMLElement | null>(null);
  const { isDesktop, isReady } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["video"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx("content")}>
          {showHeader && (
            <SectionHeader
              largeTitle={largeTitle}
              eyebrow={eyebrow}
              headline={headline}
              subhead={body}
              isInView={isInView}
              isRevealEnabled={isRevealEnabled}
            />
          )}
          {mounted && isReady && (
            <>
              {isDesktop ? (
                <VideoPlaylistDesktop {...props} />
              ) : (
                <VideoPlaylistMobile {...props} />
              )}
            </>
          )}
        </Container>
      </RevealWrapper>
    </section>
  );
}

// ---------- GraphQL fragment

Video.fragments = {
  entry: gql`
    fragment VideoPlaylist on FlexiblePageContentContentVideoPlaylistLayout {
      eyebrow
      headline
      body
      primaryVideo {
        performanceName
        videoType
        youtubeVideoId
        videoUrl
        previewVideoUrl
        videoThumbnail {
          ...ACFMedia
        }
        youtubeThumbnail {
          ...ACFMedia
        }
      }
      videos {
        performanceName
        videoType
        youtubeVideoId
        videoUrl
        videoThumbnail {
          ...ACFMedia
        }
        youtubeThumbnail {
          ...ACFMedia
        }
      }
    }
  `
};
